import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import { isNative as isNativePlatform } from '../utils/nativeTokenStore';
import { toast } from 'sonner';
import { TOAST } from '../utils/toastMessages';
import { Eye, EyeOff, Mail, Lock, User, Shield, UserPlus, LogIn, KeyRound, AlertTriangle, Calendar, Briefcase, ChevronRight, SkipForward, Music, Camera, Utensils, Building2, Palette, PartyPopper, MapPin, Locate, ArrowRight, ArrowLeft, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { CeleyLoader } from '../components/ui/CeleyLoader';
import {
  VENDOR_TYPE_KEYS,
  VENDOR_TYPE_META,
  VENDOR_TYPE_TO_ALLOWED_CATEGORIES,
} from '../constants/vendorTypes';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const safeArray = (arr) => Array.isArray(arr) ? arr : [];

const VENDOR_SERVICES = [
  { value: 'DJ', label: 'DJ', icon: Music },
  { value: 'Photographer', label: 'Photographer', icon: Camera },
  { value: 'Videographer', label: 'Videographer', icon: Camera },
  { value: 'Caterer', label: 'Catering', icon: Utensils },
  { value: 'Venue', label: 'Venue', icon: Building2 },
  { value: 'Balloon Artist', label: 'Balloons', icon: PartyPopper },
  { value: 'Event Decor & Styling', label: 'Decor', icon: Palette },
  { value: 'Photo Booth Provider', label: 'Photo Booth', icon: Camera },
  { value: 'Face Painters', label: 'Face Painting', icon: Palette },
];

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [processingSession, setProcessingSession] = useState(false);
  const [preselectedRole, setPreselectedRole] = useState(null);
  // B-SEC-6.25 — Concierge share-token captured from URL (`?t=`) or LS.
  // Sent to /auth/register or /auth/login as `concierge_share_token`;
  // server-side claim binds the event to the authed user and returns
  // `next_event_id` for redirect. Frontend never trusts a client-supplied
  // event_id for authorization.
  const [conciergeToken, setConciergeToken] = useState('');
  // Guards the already-authenticated `?t=` adoption so it runs at most
  // once per page lifetime and never alongside an inline submit claim.
  const authedClaimHandledRef = useRef(false);
  
  // Post-signup flow state
  const [postSignupStep, setPostSignupStep] = useState(null); // null | 'role' | 'security' | 'vendorSetup'
  const [signupToken, setSignupToken] = useState(null);
  const [signupUser, setSignupUser] = useState(null);
  
  // Security questions
  const [securityQuestions, setSecurityQuestions] = useState([]);
  const [securityAnswers, setSecurityAnswers] = useState([
    { question: '', answer: '' },
    { question: '', answer: '' },
    { question: '', answer: '' }
  ]);

  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordStep, setForgotPasswordStep] = useState(1);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordQuestions, setForgotPasswordQuestions] = useState([]);
  const [forgotPasswordAnswers, setForgotPasswordAnswers] = useState(['', '', '']);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState('');
  const [forgotPasswordDeadEnd, setForgotPasswordDeadEnd] = useState(false);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    ref_code: '',
  });

  const { login, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    document.documentElement.classList.add('auth-page-active');
    document.body.classList.add('auth-page-active');
    return () => {
      document.documentElement.classList.remove('auth-page-active');
      document.body.classList.remove('auth-page-active');
    };
  }, []);

  const redirectAfterAuth = useCallback((userData) => {
    const savedRedirect = localStorage.getItem('authRedirect');
    if (savedRedirect) {
      localStorage.removeItem('authRedirect');
      navigate(savedRedirect, { replace: true });
      return;
    }
    if (userData.is_admin) {
      navigate('/admin/dashboard', { replace: true });
    } else if (userData.role === 'vendor') {
      navigate('/vendor/dashboard', { replace: true });
    } else {
      navigate('/customer-dashboard', { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const error = urlParams.get('error');
    const roleParam = urlParams.get('role');
    const modeParam = urlParams.get('mode');
    const refCode = urlParams.get('ref');
    const emailParam = urlParams.get('email');
    // B-SEC-6.25 — Concierge share-token from URL (`t=`). Used as the
    // authoritative adoption proof; passed through signup/login and to
    // the explicit /auth/claim-concierge-event endpoint.
    const conciergeTokenParam = (urlParams.get('t') || '').trim();
    const intentParam = (urlParams.get('intent') || '').trim();

    if (window.location.pathname === '/signup') {
      setIsLogin(false);
    }

    if (modeParam === 'signup') setIsLogin(false);
    else if (modeParam === 'login') setIsLogin(true);

    // Phase 8.6B (F3) — Concierge intake forwards the customer's email
    // into the signup form via the URL param. Falls back to localStorage
    // so a customer who closes the tab between submitted page and
    // signup still sees their email pre-filled.
    let prefillEmail = (emailParam || '').trim();
    if (!prefillEmail) {
      try { prefillEmail = (localStorage.getItem('celey:concierge:email') || '').trim(); } catch (_) { /* ignore */ }
    }
    if (prefillEmail) {
      setFormData(prev => ({ ...prev, email: prefillEmail }));
      // If we have an email pre-fill, the customer is in the signup
      // path by intent — switch the toggle to signup so they don't
      // land on the login form.
      if (modeParam !== 'login') setIsLogin(false);
    }

    // B-SEC-6.25 — pull the share-token from either the URL or LS and
    // remember it in state. Prefill email from the server-side lookup
    // when only the token is known (cross-device / incognito recovery).
    let tokenForClaim = conciergeTokenParam;
    if (!tokenForClaim) {
      try { tokenForClaim = (localStorage.getItem('celey:concierge:token') || '').trim(); } catch (_) { /* ignore */ }
    }
    if (tokenForClaim) {
      setConciergeToken(tokenForClaim);
      // If we have a token but no prefilled email, resolve the email
      // from the server so the login/signup form is ready to submit.
      // Fires anonymously; the token itself is the proof-of-possession.
      if (!prefillEmail) {
        axios.get(`${API}/auth/lookup-intake`, { params: { token: tokenForClaim } })
          .then(res => {
            const em = (res?.data?.email || '').trim();
            if (em) setFormData(prev => (prev.email ? prev : { ...prev, email: em }));
          })
          .catch(() => { /* invalid/expired token — proceed with normal auth */ });
      }
      // `intent=claim` is a UX hint from the Concierge Submitted page
      // that the customer is here to connect an existing account.
      if (intentParam === 'claim' && modeParam !== 'signup') {
        setIsLogin(true);
      }
    }

    // Capture referral code from URL silently
    try {
      if (refCode) {
        setFormData(prev => ({ ...prev, ref_code: refCode.trim() }));
        setIsLogin(false);
      }
    } catch (e) {
      console.error('Error processing referral code:', e);
    }

    // Pre-select vendor role from URL (e.g., /auth?mode=signup&role=vendor)
    if (roleParam === 'vendor') {
      setPreselectedRole('vendor');
    }

    if (error === 'invalid_session') {
      toast.error('Authentication failed. Please try again.');
      window.history.replaceState(null, '', window.location.pathname);
    } else if (sessionId && !processingSession) {
      processGoogleSession(sessionId);
    }
  }, []);

  useEffect(() => {
    // Feb 2026: belt-and-braces guard against the logout race.
    // If logout cleared localStorage.token but React user state is briefly
    // still truthy (microtask boundary inside logout()'s async chain),
    // AuthPage must NOT redirect back to dashboard. Token presence is the
    // canonical signal that an authenticated session actually exists.
    if (user && localStorage.getItem('token') && !postSignupStep) {
      // Already-authenticated Concierge adoption: a customer following a
      // brief/intake link (`?t=`) while logged in must not have the token
      // silently discarded by the dashboard redirect. Run the existing
      // claim endpoint (idempotent, email-belt enforced server-side) and
      // land on the adopted event. Skipped when the login/signup submit
      // already handled the claim inline.
      const urlClaimToken = (new URLSearchParams(window.location.search).get('t') || '').trim();
      if (urlClaimToken && !authedClaimHandledRef.current) {
        authedClaimHandledRef.current = true;
        (async () => {
          try {
            const res = await axios.post(
              `${API}/auth/claim-concierge-event`,
              { share_token: urlClaimToken },
              { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
            );
            const adoptedEventId = res?.data?.event_id;
            if (adoptedEventId) {
              toast.success(res?.data?.already_claimed
                ? 'Welcome back! Your Concierge request is here.'
                : 'We connected your Concierge request to your account.');
              navigate(`/events/${adoptedEventId}`, { replace: true });
              return;
            }
          } catch (err) {
            const detail = err?.response?.data?.detail;
            const message = (detail && typeof detail === 'object' ? detail.message : detail) || '';
            toast.error(message || 'We could not connect your Concierge request. Please try again from your link.');
          }
          redirectAfterAuth(user);
        })();
        return;
      }
      redirectAfterAuth(user);
    }
  }, [user, navigate, redirectAfterAuth, postSignupStep]);

  const processGoogleSession = async (sessionId) => {
    setProcessingSession(true);
    try {
      const response = await axios.post(`${API}/auth/process-session`, null, {
        params: { session_id: sessionId }
      });
      login(response.data.user, response.data.session_token);
      toast.success('Welcome! Logged in with Google');
      window.history.replaceState(null, '', window.location.pathname);
      redirectAfterAuth(response.data.user);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setProcessingSession(false);
    }
  };

  // Fetch security questions for post-signup recovery setup
  const fetchSecurityQuestions = async () => {
    try {
      const response = await axios.get(`${API}/auth/security-questions`);
      const questions = safeArray(response?.data?.questions);
      setSecurityQuestions(questions);
      if (questions.length > 0) {
        setSecurityAnswers(questions.map(q => ({ question: q, answer: '' })));
      }
    } catch (error) {
      console.error('Failed to load security questions:', error);
    }
  };

  // Forgot password handlers
  const handleForgotPasswordEmailSubmit = async () => {
    if (!forgotPasswordEmail.trim()) {
      toast.error('Please enter your email address');
      return;
    }
    setForgotPasswordLoading(true);
    setForgotPasswordError('');
    setForgotPasswordDeadEnd(false);
    try {
      const response = await axios.post(`${API}/auth/forgot-password/questions`, {
        email: forgotPasswordEmail
      });
      const questions = safeArray(response?.data?.questions);
      setForgotPasswordQuestions(questions);
      setForgotPasswordAnswers(questions.map(() => ''));
      setForgotPasswordStep(2);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Email not found';
      setForgotPasswordError(errorMsg);
      // 400 from this endpoint = account has no security questions on file:
      // a terminal state for self-service recovery, so hide Continue.
      if (error.response?.status === 400) setForgotPasswordDeadEnd(true);
      if (errorMsg.length < 100) toast.error(errorMsg);
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleVerifySecurityAnswers = async () => {
    if (forgotPasswordAnswers.some(a => !a.trim())) {
      toast.error('Please answer all security questions');
      return;
    }
    setForgotPasswordLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password/verify`, {
        email: forgotPasswordEmail,
        answers: safeArray(forgotPasswordQuestions).map((q, i) => ({
          question: q,
          answer: forgotPasswordAnswers[i] ?? ''
        }))
      });
      setForgotPasswordStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Security answers incorrect');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setForgotPasswordLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password/reset`, {
        email: forgotPasswordEmail,
        answers: safeArray(forgotPasswordQuestions).map((q, i) => ({
          question: q,
          answer: forgotPasswordAnswers[i] ?? ''
        })),
        new_password: newPassword
      });
      toast.success('Password reset successfully! Please log in.');
      closeForgotPasswordDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const closeForgotPasswordDialog = () => {
    setShowForgotPassword(false);
    setForgotPasswordStep(1);
    setForgotPasswordEmail('');
    setForgotPasswordQuestions([]);
    setForgotPasswordAnswers(['', '', '']);
    setNewPassword('');
    setConfirmNewPassword('');
    setForgotPasswordError('');
    setForgotPasswordDeadEnd(false);
  };

  // Main form submit (login or signup)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    // The submit path performs the claim inline (server-side, as part of
    // login/register) — mark it handled so the authenticated-session
    // effect above never double-claims or double-toasts.
    if (conciergeToken) authedClaimHandledRef.current = true;

    // B-SEC-6.25 helper — after a successful login/signup, clear all
    // Concierge intake stashes so a stale event_id never later hijacks a
    // redirect for an unrelated session. Runs on every successful auth,
    // Concierge or not — a no-op when nothing was stashed.
    const clearConciergeAdoptionStash = () => {
      try { localStorage.removeItem('celey:concierge:email'); } catch (_) { /* ignore */ }
      try { localStorage.removeItem('celey:concierge:event_id'); } catch (_) { /* ignore */ }
      try { localStorage.removeItem('celey:concierge:token'); } catch (_) { /* ignore */ }
    };

    try {
      if (isLogin) {
        // Native iOS/Android: include X-Client-Platform so backend returns
        // `refresh_token` in body (alongside the cookie). Web omits the
        // header → response body is byte-identical to the old contract.
        const loginHeaders = isNativePlatform() ? { 'X-Client-Platform': 'native' } : {};
        const loginBody = {
          email: formData.email,
          password: formData.password,
        };
        if (conciergeToken) loginBody.concierge_share_token = conciergeToken;
        const response = await axios.post(`${API}/auth/login`, loginBody, { headers: loginHeaders, withCredentials: true });

        // B-SEC-6.25 — server-driven redirect. `next_event_id` is only
        // present when the share-token was valid and the claim succeeded
        // for the authed user. Never trust a client-controlled event_id.
        const claim = response.data?.concierge_claim;
        if (claim?.status === 'ok' && response.data?.next_event_id) {
          try { localStorage.setItem('authRedirect', `/events/${response.data.next_event_id}`); } catch (_) { /* ignore */ }
          toast.success(claim.already_claimed
            ? 'Welcome back! Your Concierge request is here.'
            : 'We connected your Concierge request to your account.');
          clearConciergeAdoptionStash();
        } else {
          if (claim?.status === 'failed' && conciergeToken) {
            // Adoption attempted but rejected — surface the reason but
            // still allow the user into their account.
            toast.error(claim.detail || 'We could not connect your Concierge request. Please try again from your link.');
          } else {
            toast.success('Welcome back!');
          }
          clearConciergeAdoptionStash();
        }
        login(response.data.user, response.data.token, response.data.refresh_token);
      } else {
        // Simplified signup: name, email, password only
        const registerBody = {
          name: formData.name,
          email: formData.email,
          password: formData.password,
          ref_code: formData.ref_code || undefined,
        };
        if (conciergeToken) registerBody.concierge_share_token = conciergeToken;
        const response = await axios.post(`${API}/auth/register`, registerBody);
        const { token, user: newUser } = response.data;

        if (token && newUser) {
          sessionStorage.setItem('celey:new_signup', 'true');
          // Store token and user for post-signup steps
          setSignupToken(token);
          setSignupUser(newUser);

          // B-SEC-6.25 — Concierge adoption short-circuit.
          // The trust anchor is the SERVER's response `concierge_claim`,
          // not localStorage. When the server confirms an adoption, skip
          // role-selection + security-questions and route directly to the
          // adopted event. LocalStorage is only used as a UX accelerator
          // for the same-browser happy path and never as authorization.
          const claim = response.data?.concierge_claim;
          const isConciergeAdoption =
            preselectedRole !== 'vendor'
            && claim?.status === 'ok'
            && !!response.data?.next_event_id;
          if (isConciergeAdoption) {
            try {
              const roleRes = await axios.post(`${API}/auth/select-role`, { role: 'customer' }, {
                headers: { Authorization: `Bearer ${token}` }
              });
              const finalToken = roleRes.data.token || token;
              const finalUser = roleRes.data.user || newUser;
              clearConciergeAdoptionStash();
              // Pin the server-confirmed event_id as the post-auth
              // redirect target. This value came from the server's
              // share-token resolution, not client-controlled state.
              try {
                localStorage.setItem('authRedirect', `/events/${response.data.next_event_id}`);
              } catch (_) { /* ignore */ }
              setPostSignupStep(null);
              login(finalUser, finalToken);
              toast.success('We connected your Concierge request to your account.');
              return;
            } catch (_err) {
              // Role assignment failed — fall back to the standard
              // post-signup pipeline so the user isn't left dangling.
              setPostSignupStep('role');
              fetchSecurityQuestions();
              return;
            }
          }

          // If a Concierge token was supplied but the claim failed, tell
          // the user why so they can recover (usually: signed up with a
          // different email than the one used at intake).
          if (conciergeToken && claim?.status === 'failed') {
            toast.error(claim.detail || 'We could not connect your Concierge request to this account.');
          }
          clearConciergeAdoptionStash();

          // If vendor role pre-selected (from /for-vendors CTA), skip role selection
          if (preselectedRole === 'vendor') {
            try {
              const roleRes = await axios.post(`${API}/auth/select-role`, { role: 'vendor' }, {
                headers: { Authorization: `Bearer ${token}` }
              });
              setSignupToken(roleRes.data.token);
              setSignupUser(roleRes.data.user);
              setPostSignupStep('security');
            } catch {
              setPostSignupStep('role');
            }
          } else {
            setPostSignupStep('role');
          }
          // Pre-fetch security questions
          fetchSecurityQuestions();
        } else {
          toast.success('Account created! Please log in.');
          setIsLogin(true);
        }
      }
    } catch (error) {
      console.error('Auth error:', error?.message, error?.response?.data);
      // B-SEC-6.25 — better recovery when a Concierge signup collides
      // with an existing account: offer a direct switch to login while
      // preserving the share-token so the claim runs after login.
      const detail = error.response?.data?.detail;
      if (!isLogin && conciergeToken && typeof detail === 'string' && /already registered/i.test(detail)) {
        toast.error('This email already has a Celey account. Please log in to connect your Concierge request.');
        setIsLogin(true);
        // Keep formData.email and conciergeToken as-is so the user can
        // just enter their password and submit.
      } else {
        toast.error(typeof detail === 'string' ? detail : (detail?.message || 'An error occurred'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Role selection handler
  const handleRoleSelect = async (role) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/select-role`, { role }, {
        headers: { Authorization: `Bearer ${signupToken}` }
      });
      // Update token and user with new role
      setSignupToken(response.data.token);
      setSignupUser(response.data.user);
      // Move to security setup step
      setPostSignupStep('security');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set role');
    } finally {
      setLoading(false);
    }
  };

  // Security questions save handler
  const handleSaveSecurity = async () => {
    const incomplete = securityAnswers.some(sa => !sa.answer.trim());
    if (incomplete) {
      toast.error('Please answer all 3 security questions');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/setup-security`, {
        security_answers: securityAnswers
      }, {
        headers: { Authorization: `Bearer ${signupToken}` }
      });
      toast.success('Security questions saved!');
      // Complete signup flow
      finishSignup();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save security questions');
    } finally {
      setLoading(false);
    }
  };

  // Skip security and finish
  const handleSkipSecurity = () => {
    finishSignup();
  };

  const finishSignup = () => {
    // If vendor, show quick setup before dashboard
    const roles = signupUser?.roles || [signupUser?.role];
    if (roles.includes('vendor')) {
      setPostSignupStep('vendorSetup');
      return;
    }
    login(signupUser, signupToken);
    toast.success(TOAST.WELCOME_CUSTOMER);
    setPostSignupStep(null);
    // Redirect handled by useEffect watching user
  };

  // ===== RENDER: Post-signup Role Selection =====
  if (postSignupStep === 'role') {
    return (
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#f9fafb' }}>
        <main className="flex-1 flex flex-col items-center justify-center p-4">
          <div className="w-full max-w-md mx-auto md:rounded-xl md:border md:shadow-2xl bg-white p-6 md:p-8">
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <img src="/celeyapp-icon-green.png" alt="Celey" className="h-12 w-12" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900" data-testid="role-selection-title">
                What would you like to do on Celey?
              </h1>
              <p className="text-sm text-gray-500 mt-1">Choose how you want to get started</p>
            </div>

            <div className="space-y-3">
              <button
                type="button"
                onClick={() => handleRoleSelect('customer')}
                disabled={loading}
                className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-gray-200 hover:border-emerald-500 hover:bg-emerald-50/50 transition-all group text-left"
                data-testid="role-plan-event"
              >
                <div className="h-12 w-12 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0 group-hover:bg-emerald-200 transition-colors">
                  <Calendar className="h-6 w-6 text-emerald-700" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">Plan an Event</p>
                  <p className="text-sm text-gray-500">Find and book local service providers</p>
                </div>
                <ChevronRight className="h-5 w-5 text-gray-300 group-hover:text-emerald-500 transition-colors" />
              </button>

              <button
                type="button"
                onClick={() => handleRoleSelect('vendor')}
                disabled={loading}
                className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-gray-200 hover:border-blue-500 hover:bg-blue-50/50 transition-all group text-left"
                data-testid="role-offer-services"
              >
                <div className="h-12 w-12 rounded-xl bg-blue-100 flex items-center justify-center shrink-0 group-hover:bg-blue-200 transition-colors">
                  <Briefcase className="h-6 w-6 text-blue-700" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">Offer My Services</p>
                  <p className="text-sm text-gray-500">Grow your business and find clients</p>
                </div>
                <ChevronRight className="h-5 w-5 text-gray-300 group-hover:text-blue-500 transition-colors" />
              </button>
            </div>

            {loading && (
              <div className="flex justify-center mt-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600"></div>
              </div>
            )}
          </div>
        </main>
      </div>
    );
  }

  // ===== RENDER: Post-signup Security Setup =====
  if (postSignupStep === 'security') {
    return (
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#f9fafb' }}>
        <main className="flex-1 flex flex-col items-center p-4 overflow-y-auto">
          <div className="w-full max-w-md mx-auto md:rounded-xl md:border md:shadow-2xl bg-white p-6 md:p-8 my-4 md:my-8">
            <div className="text-center mb-6">
              <div className="flex justify-center mb-4">
                <div className="h-14 w-14 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Shield className="h-7 w-7 text-emerald-600" />
                </div>
              </div>
              <h1 className="text-2xl font-bold text-gray-900" data-testid="security-setup-title">
                Secure your account
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Add recovery questions to protect your account
              </p>
            </div>

            <div className="space-y-4">
              {safeArray(securityAnswers).map((sa, index) => (
                <div key={index}>
                  <Label className="text-sm font-medium text-gray-700" data-testid={`security-q-label-${index}`}>
                    {sa.question || `Question ${index + 1}`}
                  </Label>
                  <Input
                    type="text"
                    placeholder="Your answer"
                    value={sa.answer}
                    onChange={(e) => {
                      const newAnswers = [...securityAnswers];
                      newAnswers[index] = { ...newAnswers[index], answer: e.target.value };
                      setSecurityAnswers(newAnswers);
                    }}
                    className="mt-1"
                    data-testid={`security-answer-${index}`}
                  />
                </div>
              ))}
            </div>

            <div className="mt-6 space-y-3">
              <Button
                onClick={handleSaveSecurity}
                disabled={loading}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-3"
                data-testid="save-security-btn"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Saving...
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2">
                    <Shield className="h-4 w-4" />
                    Save & Continue
                  </div>
                )}
              </Button>

              <button
                type="button"
                onClick={handleSkipSecurity}
                className="w-full text-gray-500 hover:text-gray-700 text-sm py-2 flex items-center justify-center gap-1 transition-colors"
                data-testid="skip-security-btn"
              >
                <SkipForward className="h-4 w-4" />
                Skip for now
              </button>
              <p className="text-xs text-center text-gray-400">
                You can set this up later in your account settings
              </p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // ===== RENDER: Vendor Quick Setup =====
  if (postSignupStep === 'vendorSetup') {
    return <VendorQuickSetupScreen signupToken={signupToken} signupUser={signupUser} login={login} setPostSignupStep={setPostSignupStep} />;
  }

  // Feb 2026: native-cold-start race fix. While auth bootstrap is still
  // checking for a valid stored session, render the neutral loader instead
  // of the login/signup form. Without this gate, native iOS WKWebView users
  // (refresh flow can take 2-8s) reach AuthPage, begin typing credentials,
  // and then get jolted into the dashboard mid-typing when checkAuth()
  // finally resolves a valid session.
  if (authLoading) {
    return <CeleyLoader testid="auth-bootstrap-loader" />;
  }


  // ===== RENDER: Main Auth (Login / Signup) =====
  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#f9fafb' }}>
      <main className="flex-1 flex flex-col items-center p-4 overflow-y-auto" style={{ backgroundColor: '#f9fafb' }}>
        {processingSession ? (
          <div className="text-center mt-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Completing authentication...</p>
          </div>
        ) : (
          <div className="w-full max-w-md mx-auto md:rounded-xl md:border md:shadow-2xl bg-white mt-4 mb-8 md:my-8">
            <div className="text-center space-y-2 pb-4 pt-6 md:pb-6 px-4 md:px-6">
              <div className="flex justify-center mb-4">
                <div
                  className="cursor-pointer"
                  onClick={() => navigate('/')}
                  title="Back to home"
                >
                  <img
                    src="/celeyapp-icon-green.png"
                    alt="Celey"
                    className="h-12 w-12 md:h-14 md:w-14 hover:scale-105 transition-transform"
                  />
                </div>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-emerald-600 to-blue-600 bg-clip-text text-transparent" data-testid="auth-title">
                {isLogin ? 'Welcome Back' : 'Join Celey'}
              </h1>
              <p className="text-sm text-gray-500">
                {isLogin ? 'Sign in to continue' : 'Create your account in seconds'}
              </p>
            </div>

            <div className="px-4 pb-6 md:px-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                {!isLogin && (
                  <div>
                    <Label htmlFor="name" className="text-sm font-medium text-gray-700 md:block hidden">Full Name</Label>
                    <div className="relative mt-1">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <Input
                        id="name"
                        type="text"
                        placeholder="Your name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="pl-10"
                        required
                        data-testid="signup-name"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <Label htmlFor="email" className="text-sm font-medium text-gray-700">Email</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="pl-10"
                      required
                      data-testid="auth-email"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="password" className="text-sm font-medium text-gray-700">Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="pl-10 pr-10"
                      required
                      data-testid="auth-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  {isLogin && (
                    <div className="flex justify-end mt-1">
                      <button
                        type="button"
                        onClick={() => setShowForgotPassword(true)}
                        className="text-sm text-emerald-600 hover:text-emerald-700 hover:underline"
                      >
                        Forgot password?
                      </button>
                    </div>
                  )}
                  {!isLogin && (
                    <p className="text-xs text-gray-400 mt-1">Use at least 6 characters</p>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-emerald-600 to-blue-600 hover:from-emerald-700 hover:to-blue-700 text-white font-medium py-3 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl"
                  data-testid="auth-submit-btn"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      {isLogin ? 'Signing In...' : 'Creating Account...'}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      {isLogin ? <LogIn className="h-5 w-5 mr-2" /> : <UserPlus className="h-5 w-5 mr-2" />}
                      {isLogin ? 'Sign In' : 'Create Account'}
                    </div>
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <button
                  type="button"
                  onClick={() => setIsLogin(!isLogin)}
                  className="text-sm text-emerald-600 hover:text-emerald-700 font-medium transition-colors"
                  data-testid="auth-toggle-mode"
                >
                  {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
                </button>
              </div>

              {/* Legal links — required by Apple Guideline 5.1.1 to be
                  reachable at the point of account creation. */}
              <div className="mt-4 pb-2 text-center">
                <p className="text-[11px] text-gray-500 leading-snug">
                  {isLogin ? 'By signing in' : 'By creating an account'} you agree to our{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/terms')}
                    className="underline hover:text-gray-700"
                    data-testid="auth-terms-link"
                  >
                    Terms of Service
                  </button>
                  {' '}and{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/privacy')}
                    className="underline hover:text-gray-700"
                    data-testid="auth-privacy-link"
                  >
                    Privacy Policy
                  </button>
                  .
                </p>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={(open) => !open && closeForgotPasswordDialog()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-emerald-600" />
              Reset Password
            </DialogTitle>
            <DialogDescription>
              {forgotPasswordStep === 1 && "Enter your email to reset your password."}
              {forgotPasswordStep === 2 && "Answer your security questions to verify your identity."}
              {forgotPasswordStep === 3 && "Create a new password for your account."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {forgotPasswordStep === 1 && (
              <>
                <div>
                  <Label htmlFor="forgot-email">Email Address</Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    value={forgotPasswordEmail}
                    onChange={(e) => {
                      setForgotPasswordEmail(e.target.value);
                      setForgotPasswordError('');
                      setForgotPasswordDeadEnd(false);
                    }}
                    placeholder="Enter your email"
                    className="mt-1"
                  />
                </div>
                {forgotPasswordError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-700">{forgotPasswordError}</p>
                  </div>
                )}
                {!forgotPasswordDeadEnd && (
                  <Button
                    onClick={handleForgotPasswordEmailSubmit}
                    disabled={forgotPasswordLoading}
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                  >
                    {forgotPasswordLoading ? 'Checking...' : 'Continue'}
                  </Button>
                )}
              </>
            )}

            {forgotPasswordStep === 2 && (
              <>
                <p className="text-sm text-gray-600 mb-4">
                  Please answer the security questions you set up when creating your account.
                </p>
                {safeArray(forgotPasswordQuestions).length > 0 ? (
                  safeArray(forgotPasswordQuestions).map((question, index) => (
                    <div key={index}>
                      <Label className="text-sm font-medium text-gray-700">{question || `Question ${index + 1}`}</Label>
                      <Input
                        type="text"
                        value={forgotPasswordAnswers[index] ?? ''}
                        onChange={(e) => {
                          const newAnswers = [...forgotPasswordAnswers];
                          newAnswers[index] = e.target.value;
                          setForgotPasswordAnswers(newAnswers);
                        }}
                        placeholder="Your answer"
                        className="mt-1"
                      />
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500">Loading questions...</p>
                )}
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" onClick={() => setForgotPasswordStep(1)} className="flex-1">
                    Back
                  </Button>
                  <Button
                    onClick={handleVerifySecurityAnswers}
                    disabled={forgotPasswordLoading}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  >
                    {forgotPasswordLoading ? 'Verifying...' : 'Verify'}
                  </Button>
                </div>
              </>
            )}

            {forgotPasswordStep === 3 && (
              <>
                <div>
                  <Label htmlFor="new-password">New Password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="confirm-password">Confirm Password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    placeholder="Confirm new password"
                    className="mt-1"
                  />
                </div>
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" onClick={() => setForgotPasswordStep(2)} className="flex-1">
                    Back
                  </Button>
                  <Button
                    onClick={handleResetPassword}
                    disabled={forgotPasswordLoading}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  >
                    {forgotPasswordLoading ? 'Resetting...' : 'Reset Password'}
                  </Button>
                </div>
              </>
            )}
          </div>
          <p className="text-xs text-gray-500 border-t border-gray-100 pt-3" data-testid="forgot-password-support-contact">
            Need help accessing your account? Contact Celey Support at{' '}
            <a href="mailto:customersupport@celeyapp.com" className="text-emerald-600 underline hover:text-emerald-700" data-testid="support-email-link">
              customersupport@celeyapp.com
            </a>.
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuthPage;

// ===== Vendor Quick Setup Component =====
function VendorQuickSetupScreen({ signupToken, signupUser, login, setPostSignupStep }) {
  const navigate = useNavigate();
  // Phase 5B: 2-step signup. Step 1 — pick vendor_type (identity).
  // Step 2 — pick categories filtered to that type (discovery). Location
  // + starting-price live with the categories step so users don't have to
  // backtrack. Both steps keep `vendor.categories` populated so templates,
  // matching, and onboarding continue to work unchanged.
  const [step, setStep] = useState('type');
  const [vendorType, setVendorType] = useState(null);
  const [selectedServices, setSelectedServices] = useState([]);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [setupZip, setSetupZip] = useState('');
  const [setupCity, setSetupCity] = useState('');
  const [setupState, setSetupState] = useState('');
  const [startingPrice, setStartingPrice] = useState('');
  const [saving, setSaving] = useState(false);
  // Tracks the in-flight ZIP→city/state lookup so handleContinue can await
  // it before posting. Without this, a user who taps Continue immediately
  // after typing the 5th ZIP digit could submit `city`/`state` undefined.
  const zipLookupRef = useRef(null);

  const toggleService = (value) => {
    setSelectedServices(prev =>
      prev.includes(value) ? prev.filter(s => s !== value) : [...prev, value]
    );
  };

  const lookupZip = (zip) => {
    if (!/^\d{5}$/.test(zip)) return null;
    const promise = (async () => {
      try {
        const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
        if (res.ok) {
          const data = await res.json();
          const place = data.places?.[0];
          if (place) {
            const resolvedCity = place['place name'] || '';
            const resolvedState = place['state abbreviation'] || '';
            setSetupCity(resolvedCity);
            setSetupState(resolvedState);
            return { city: resolvedCity, state: resolvedState };
          }
        }
      } catch { /* ignore — backend safety net will derive on POST */ }
      return null;
    })();
    zipLookupRef.current = promise;
    return promise;
  };

  const handleContinue = async () => {
    if (selectedServices.length === 0) {
      toast.error('Select at least one service');
      return;
    }
    if (!setupZip.trim() && !setupCity.trim()) {
      toast.error('Add your location');
      return;
    }
    setSaving(true);
    // Race fix: if a ZIP lookup is still in flight (user tapped Continue
    // immediately after typing the 5th digit), await it so city/state are
    // populated before posting. Falls back to whatever the lookup returned
    // if React's setState hasn't flushed yet.
    let cityForPost = setupCity;
    let stateForPost = setupState;
    if (zipLookupRef.current) {
      try {
        const resolved = await zipLookupRef.current;
        if (resolved) {
          if (!cityForPost && resolved.city) cityForPost = resolved.city;
          if (!stateForPost && resolved.state) stateForPost = resolved.state;
        }
      } catch { /* ignore — backend safety net will derive on POST */ }
    }
    try {
      await axios.post(`${API}/vendors/quick-setup`, {
        categories: selectedServices,
        vendor_type: vendorType || undefined,
        zip_code: setupZip,
        city: cityForPost || undefined,
        state: stateForPost || undefined,
        starting_price: startingPrice || undefined,
      }, {
        headers: { Authorization: `Bearer ${signupToken}` }
      });
      login(signupUser, signupToken);
      // No toast here — VendorDashboard fires the single welcome toast
      // on mount via the `celey:new_signup` sessionStorage flag set by
      // the auth login flow. Firing both stacks two near-identical toasts
      // ~500ms apart with `visibleToasts: 3`.
      setPostSignupStep(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Setup failed — you can complete this later');
      login(signupUser, signupToken);
      setPostSignupStep(null);
    } finally {
      setSaving(false);
    }
  };

  const canContinue = selectedServices.length > 0 && (setupZip.trim() || setupCity.trim());

  // Step 1 — business type picker
  if (step === 'type') {
    return (
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#f9fafb' }}>
        <main className="flex-1 flex flex-col items-center p-4 overflow-y-auto">
          <div className="w-full max-w-md mx-auto md:rounded-xl md:border md:shadow-2xl bg-white p-6 md:p-8 my-4 md:my-8">
            <div className="text-center mb-6">
              <div className="flex justify-center mb-4">
                <img src="/celeyapp-icon-green.png" alt="Celey" className="h-10 w-10" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900" data-testid="vendor-type-title">
                What best describes your business?
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                We&apos;ll tailor the setup to your type of work.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-2" data-testid="vendor-type-grid">
              {VENDOR_TYPE_KEYS.map(key => {
                const meta = VENDOR_TYPE_META[key];
                const Icon = meta.icon;
                const isSelected = vendorType === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setVendorType(key)}
                    data-testid={`vendor-type-${key}`}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-all ${
                      isSelected
                        ? 'border-emerald-600 bg-emerald-50/80'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${
                      isSelected ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600'
                    }`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm font-semibold ${isSelected ? 'text-emerald-950' : 'text-gray-900'}`}>
                        {meta.label}
                      </p>
                      <p className="text-[11px] text-gray-500 truncate">{meta.description}</p>
                    </div>
                    {isSelected && <Check className="h-4 w-4 text-emerald-600 shrink-0" />}
                  </button>
                );
              })}
            </div>

            <Button
              onClick={() => setStep('categories')}
              disabled={!vendorType}
              className="w-full mt-5 py-5 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-base font-semibold disabled:opacity-50"
              data-testid="vendor-type-continue"
            >
              Continue <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          </div>
        </main>
      </div>
    );
  }

  // Step 2 — categories filtered by vendor_type + location + starting price
  const typeCategories = VENDOR_TYPE_TO_ALLOWED_CATEGORIES[vendorType] || [];
  const allOtherCategories = Object.values(VENDOR_TYPE_TO_ALLOWED_CATEGORIES)
    .flat()
    .filter(cat => !typeCategories.includes(cat));

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#f9fafb' }}>
      <main className="flex-1 flex flex-col items-center p-4 overflow-y-auto">
        <div className="w-full max-w-md mx-auto md:rounded-xl md:border md:shadow-2xl bg-white p-6 md:p-8 my-4 md:my-8">
          <button
            type="button"
            onClick={() => setStep('type')}
            className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 mb-4"
            data-testid="vendor-setup-back"
          >
            <ArrowLeft className="h-3 w-3" /> Back
          </button>

          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900" data-testid="vendor-setup-title">
              Set up your services
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {VENDOR_TYPE_META[vendorType]?.label} · Pick what you offer
            </p>
          </div>

          {/* Services */}
          <div className="mb-5">
            <Label className="text-sm font-semibold text-gray-700 mb-2 block">Services you offer *</Label>
            <div className="flex flex-wrap gap-2" data-testid="vendor-setup-categories">
              {typeCategories.map(cat => {
                const isSelected = selectedServices.includes(cat);
                return (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => toggleService(cat)}
                    data-testid={`setup-cat-${cat.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
                    className={`inline-flex items-center px-3 py-2 rounded-full text-xs font-medium transition-all ${
                      isSelected ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {cat}
                  </button>
                );
              })}
            </div>
            {!showAllCategories && allOtherCategories.length > 0 && (
              <button
                type="button"
                onClick={() => setShowAllCategories(true)}
                className="mt-3 text-xs text-emerald-600 hover:text-emerald-700 font-medium"
                data-testid="vendor-setup-show-all"
              >
                Don&apos;t see yours? Show all categories &rarr;
              </button>
            )}
            {showAllCategories && allOtherCategories.length > 0 && (
              <div className="mt-3">
                <p className="text-[11px] text-gray-400 mb-1.5">Other categories</p>
                <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                  {allOtherCategories.map(cat => {
                    const isSelected = selectedServices.includes(cat);
                    return (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => toggleService(cat)}
                        data-testid={`setup-cat-other-${cat.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
                        className={`inline-flex items-center px-3 py-2 rounded-full text-xs font-medium transition-all ${
                          isSelected ? 'bg-emerald-600 text-white' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        {cat}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Location */}
          <div className="mb-5">
            <Label className="text-sm font-semibold text-gray-700 mb-1 block">Service location *</Label>
            <p className="text-xs text-gray-400 mb-2">Enter your ZIP code to auto-fill city and state</p>
            <div className="grid grid-cols-3 gap-2">
              <Input
                placeholder="City"
                value={setupCity}
                onChange={(e) => setSetupCity(e.target.value)}
                data-testid="setup-city-input"
              />
              <Input
                placeholder="State"
                value={setupState}
                maxLength={2}
                onChange={(e) => setSetupState(e.target.value.toUpperCase())}
                data-testid="setup-state-input"
              />
              <Input
                placeholder="ZIP"
                value={setupZip}
                inputMode="numeric"
                maxLength={5}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '');
                  setSetupZip(val);
                  if (/^\d{5}$/.test(val)) lookupZip(val);
                }}
                data-testid="setup-zip-input"
              />
            </div>
          </div>

          {/* Starting Price */}
          <div className="mb-6">
            <Label className="text-sm font-semibold text-gray-700 mb-2 block">Starting price <span className="text-gray-400 font-normal">(optional)</span></Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
              <Input
                type="number"
                placeholder="e.g., 300"
                value={startingPrice}
                onChange={(e) => setStartingPrice(e.target.value)}
                className="pl-7"
                data-testid="setup-price-input"
              />
            </div>
          </div>

          {/* Continue */}
          <Button
            onClick={handleContinue}
            disabled={!canContinue || saving}
            className="w-full py-5 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-base font-semibold disabled:opacity-50"
            data-testid="vendor-setup-continue-btn"
          >
            {saving ? 'Saving...' : 'Continue'} {!saving && <ArrowRight className="ml-2 w-4 h-4" />}
          </Button>
        </div>
      </main>
    </div>
  );
}
